import React, { memo, useMemo } from 'react';
import { Box, Typography, Grid, Autocomplete, TextField, CircularProgress } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import ButtonMain from '../Onboarding/shared/ButtonMain';
import { getStatusIcon, textoStatus } from './PowerBIUtils';

const FiltroPowerBI = memo(
  ({ data, selected, loadingWorkspaces, onWorkspaceChange, onReportChange, onViewReportClick }) => {
    const { workspaces, reports } = data;
    const { workspace, report } = selected;

    const titleDashboard = useMemo(() => {
      if (!report) return '';
      const { descricao, datePhrase } = textoStatus(report.dadosAtualizacao);
      return descricao ? (datePhrase ? `${descricao} - ${datePhrase}` : descricao) : '';
    }, [report]);

    return (
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, mt: { xs: 1, md: 3 } }}>
        <Typography variant="body1" mb={2}>
          Utilize as opções abaixo para visualizar seus dashboards diretamente nesta página.
        </Typography>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={12} md={4}>
            <Typography variant="subtitle1" className="texto-cor-cinza-escuro label-padding">
              WorkSpace
            </Typography>
            <Autocomplete
              id="workspaces"
              options={workspaces}
              value={workspace}
              getOptionLabel={opt => opt.nome}
              loading={loadingWorkspaces}
              disabled={loadingWorkspaces}
              onChange={onWorkspaceChange}
              disablePortal
              disableClearable
              size="small"
              popupIcon={<KeyboardArrowDown className="texto-cor-complementar w-1-5em font-size-2rem" />}
              noOptionsText="Nenhum registro encontrado"
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Selecione um workspace"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingWorkspaces && <CircularProgress size={20} className="margin-right-10" />}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Typography variant="subtitle1" className="texto-cor-cinza-escuro label-padding">
              Dashboard
            </Typography>
            <Autocomplete
              id="relatorios"
              options={reports}
              value={report}
              getOptionLabel={opt => {
                const { descricao, datePhrase } = textoStatus(opt.dadosAtualizacao);
                const statusText = descricao
                  ? datePhrase
                    ? `${descricao} • ${datePhrase}`
                    : descricao
                  : '';
                return statusText ? `${opt.nome} — Status: ${statusText}` : opt.nome;
              }}
              disabled={!workspace}
              onChange={onReportChange}
              disablePortal
              disableClearable
              size="small"
              popupIcon={<KeyboardArrowDown className="texto-cor-complementar w-1-5em font-size-2rem" />}
              noOptionsText="Nenhum registro encontrado"
              renderOption={(props, opt) => {
                const { tipo, descricao, datePhrase } = textoStatus(opt.dadosAtualizacao);
                const statusText = descricao
                  ? datePhrase
                    ? `${descricao} • ${datePhrase}`
                    : descricao
                  : '';
                const label = statusText ? `${opt.nome} — Status: ${statusText}` : opt.nome;
                const tooltip = statusText || 'Sem informação de status';
                return (
                  <li {...props} title={tooltip}>
                    <Box display="flex" alignItems="center">
                      {getStatusIcon(tipo)}
                      <span>{label}</span>
                    </Box>
                  </li>
                );
              }}
              renderInput={params => {
                const status = textoStatus(report && report.dadosAtualizacao);
                const tipo = status.tipo;
                const inlineIcon = report ? getStatusIcon(tipo, { mr: 0 }) : null;

                return (
                  <Box className="posicao-relativa">
                    {inlineIcon && <Box className="iconeStatusDashboard">{inlineIcon}</Box>}
                    <TextField
                      {...params}
                      placeholder="Selecione um dashboard"
                      inputProps={{
                        ...params.inputProps,
                        title: titleDashboard
                      }}
                      // eslint-disable-next-line react/jsx-no-duplicate-props
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: null,
                        sx: {
                          ...(params.InputProps && params.InputProps.sx),
                          '& .MuiInputBase-input': {
                            marginLeft: inlineIcon ? '18px' : undefined
                          }
                        },
                        endAdornment: <>{params.InputProps.endAdornment}</>
                      }}
                    />
                  </Box>
                );
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={2}
            display="flex"
            justifyContent={{ md: 'flex-start', xs: 'center' }}
          >
            <ButtonMain
              tipoBotao="transparente-com-bordas-principal"
              className="font-size-0-875rem height-40px margin-top-38"
              disabled={!workspace || !report}
              onClick={onViewReportClick}
            >
              Ver relatório
            </ButtonMain>
          </Grid>
        </Grid>
      </Box>
    );
  }
);

export default FiltroPowerBI;
